import React, { useMemo } from 'react';
import { gql } from '@soundxyz/gql-string';
import { ReleaseCard } from '@components/SongCards/ReleasesSongCard';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { useQuery } from '@graphql/reactQuery';
import {
  AllCollectorAppearsOnReleasesDocument,
  CollectorAppearsOnUserInfoFragmentDoc,
} from '@graphql/reactQuery';
import { formatProfileHeader } from '@utils/helpers';
import { CardsContainer } from './CardsContainer';
import { SectionHeader } from './SectionHeader';
import { Subheader } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  query AllCollectorAppearsOnReleases($userId: UUID!) {
    user(id: $userId) {
      id
      publicAddress
      creditAllocations {
        id
        creditSplit {
          id
          mintedReleases {
            id
            ...ReleasesCard
          }
        }
      }
    }
  }

  fragment CollectorAppearsOnUserInfo on User {
    id
    webappUri
  }
`);

export const CollectorAppearsOnSection = ({
  user,
}: {
  user: FragmentType<CollectorAppearsOnUserInfoFragmentDoc>;
}) => {
  const { id: userId, webappUri } = getFragment(CollectorAppearsOnUserInfoFragmentDoc, user);

  const { data, isLoading: loading } = useQuery(AllCollectorAppearsOnReleasesDocument, {
    variables: {
      userId,
    },
    staleTime: Infinity,
  });

  const creditAllocations = data?.data.user?.creditAllocations;

  const releases = useMemo(
    () => creditAllocations?.flatMap(ca => ca.creditSplit.mintedReleases.map(r => r)) || [],
    [creditAllocations],
  );

  const subheader = formatProfileHeader({ count: releases.length, text: 'Releases' });

  return (
    <>
      <SectionHeader header="Appears On" userWebappUri={webappUri} />
      {loading ? (
        <div className="mt-2 h-6 w-32 animate-pulse rounded-md bg-neutral200" />
      ) : (
        <Subheader>{subheader}</Subheader>
      )}
      <CardsContainer fullWidth={false} hasFilter={false}>
        {releases?.map(release => (
          <ReleaseCard
            key={release.id}
            data={release}
            playlist={{
              type: 'HOLDER',
              associationId: userId,
            }}
          />
        ))}
      </CardsContainer>
    </>
  );
};
